// ==============================================
// Admin Settings Page - Company & Invoice Settings
// ==============================================

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import type { CompanySettings } from '../../types/subscription';
import { ScoutSignInRuleTab } from '../../components/admin/ScoutSignInRuleTab';

export function AdminSettingsPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'company' | 'invoice' | 'bank' | 'scout'>('company');
  const [formData, setFormData] = useState<Partial<CompanySettings>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch company settings
  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['admin', 'company-settings'],
    queryFn: () => api.adminGetCompanySettings(),
  });

  // Update settings mutation
  const updateMutation = useMutation({
    mutationFn: (data: Partial<CompanySettings>) => api.adminUpdateCompanySettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'company-settings'] });
      setHasChanges(false);
    },
  });

  // Initialize form data when settings load
  useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  const handleChange = (field: keyof CompanySettings, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
        Failed to load settings. Please try again.
      </div>
    );
  }

  const tabs = [
    { id: 'company' as const, label: 'Company Details' },
    { id: 'invoice' as const, label: 'Invoice & GST' },
    { id: 'bank' as const, label: 'Bank Details' },
    { id: 'scout' as const, label: 'Scout sign-in' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-slate-400 mt-1">
            Manage company details, invoice settings, and GST configuration
          </p>
        </div>

        {hasChanges && (
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            {updateMutation.isPending ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        )}
      </div>

      {/* Success/Error Messages */}
      {updateMutation.isSuccess && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 text-green-400">
          Settings saved successfully.
        </div>
      )}

      {updateMutation.isError && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
          Failed to save settings. Please try again.
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-700">
        <nav className="flex gap-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'text-cyan-400 border-cyan-400'
                  : 'text-slate-400 border-transparent hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
        {activeTab === 'company' && (
          <CompanyDetailsTab formData={formData} onChange={handleChange} />
        )}

        {activeTab === 'invoice' && (
          <InvoiceSettingsTab formData={formData} onChange={handleChange} />
        )}

        {activeTab === 'bank' && (
          <BankDetailsTab formData={formData} onChange={handleChange} />
        )}

        {activeTab === 'scout' && <ScoutSignInRuleTab />}
      </div>
    </div>
  );
}

// ==============================================
// Company Details Tab
// ==============================================

interface TabProps {
  formData: Partial<CompanySettings>;
  onChange: (field: keyof CompanySettings, value: string | number | boolean) => void;
}

function CompanyDetailsTab({ formData, onChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Company Information</h3>
        <p className="text-sm text-slate-400 mb-6">
          This information will appear on your invoices and receipts.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Company Name */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Company Name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={formData.company_name || ''}
            onChange={(e) => onChange('company_name', e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            placeholder="Your Company Ltd"
          />
        </div>

        {/* Trading Name */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Trading Name
          </label>
          <input
            type="text"
            value={formData.trading_name || ''}
            onChange={(e) => onChange('trading_name', e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            placeholder="Trading as..."
          />
        </div>

        {/* Address Line 1 */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Address Line 1
          </label>
          <input
            type="text"
            value={formData.address_line1 || ''}
            onChange={(e) => onChange('address_line1', e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            placeholder="Street address"
          />
        </div>

        {/* Address Line 2 */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Address Line 2
          </label>
          <input
            type="text"
            value={formData.address_line2 || ''}
            onChange={(e) => onChange('address_line2', e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            placeholder="Suite, floor, etc."
          />
        </div>

        {/* City */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            City
          </label>
          <input
            type="text"
            value={formData.city || ''}
            onChange={(e) => onChange('city', e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            placeholder="Auckland"
          />
        </div>

        {/* Region */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Region/State
          </label>
          <input
            type="text"
            value={formData.region || ''}
            onChange={(e) => onChange('region', e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            placeholder="Auckland"
          />
        </div>

        {/* Postal Code */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Postal Code
          </label>
          <input
            type="text"
            value={formData.postal_code || ''}
            onChange={(e) => onChange('postal_code', e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            placeholder="1010"
          />
        </div>

        {/* Country */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Country <span className="text-red-400">*</span>
          </label>
          <select
            value={formData.country || 'NZ'}
            onChange={(e) => onChange('country', e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="NZ">New Zealand</option>
            <option value="AU">Australia</option>
            <option value="US">United States</option>
            <option value="GB">United Kingdom</option>
          </select>
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Phone
          </label>
          <input
            type="tel"
            value={formData.phone || ''}
            onChange={(e) => onChange('phone', e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            placeholder="+64 9 123 4567"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Email
          </label>
          <input
            type="email"
            value={formData.email || ''}
            onChange={(e) => onChange('email', e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            placeholder="billing@company.co.nz"
          />
        </div>

        {/* Website */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Website
          </label>
          <input
            type="url"
            value={formData.website || ''}
            onChange={(e) => onChange('website', e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            placeholder="https://www.company.co.nz"
          />
        </div>
      </div>
    </div>
  );
}

// ==============================================
// Invoice & GST Settings Tab
// ==============================================

function InvoiceSettingsTab({ formData, onChange }: TabProps) {
  return (
    <div className="space-y-8">
      {/* GST Settings */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">GST Settings</h3>
        <p className="text-sm text-slate-400 mb-6">
          Configure Goods and Services Tax for New Zealand customers.
        </p>

        <div className="space-y-4">
          {/* GST Enabled Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
            <div>
              <p className="font-medium text-white">Enable GST</p>
              <p className="text-sm text-slate-400">
                Apply GST to invoices for NZ-based customers
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.gst_enabled ?? true}
                onChange={(e) => onChange('gst_enabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-cyan-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* GST Number */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                GST Number
              </label>
              <input
                type="text"
                value={formData.gst_number || ''}
                onChange={(e) => onChange('gst_number', e.target.value)}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="123-456-789"
              />
              <p className="text-xs text-slate-500 mt-1">
                Your IRD GST registration number
              </p>
            </div>

            {/* GST Rate */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                GST Rate (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.gst_rate ?? 15}
                onChange={(e) => onChange('gst_rate', parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
              <p className="text-xs text-slate-500 mt-1">
                Standard NZ GST rate is 15%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Settings */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Invoice Settings</h3>
        <p className="text-sm text-slate-400 mb-6">
          Customize invoice numbering and display options.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Invoice Prefix */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Invoice Number Prefix
            </label>
            <input
              type="text"
              value={formData.invoice_prefix || ''}
              onChange={(e) => onChange('invoice_prefix', e.target.value)}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="INV"
            />
            <p className="text-xs text-slate-500 mt-1">
              e.g., INV-000001
            </p>
          </div>

          {/* Next Invoice Number */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Next Invoice Number
            </label>
            <input
              type="number"
              min="1"
              value={formData.invoice_next_number ?? 1}
              onChange={(e) => onChange('invoice_next_number', parseInt(e.target.value) || 1)}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            <p className="text-xs text-slate-500 mt-1">
              The number for the next invoice to be created
            </p>
          </div>

          {/* Payment Terms */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Payment Terms
            </label>
            <input
              type="text"
              value={formData.invoice_payment_terms || ''}
              onChange={(e) => onChange('invoice_payment_terms', e.target.value)}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="Payment due within 7 days"
            />
          </div>

          {/* Invoice Footer */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Invoice Footer Text
            </label>
            <textarea
              value={formData.invoice_footer_text || ''}
              onChange={(e) => onChange('invoice_footer_text', e.target.value)}
              rows={3}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
              placeholder="Thank you for your business!"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ==============================================
// Bank Details Tab
// ==============================================

function BankDetailsTab({ formData, onChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Bank Account Details</h3>
        <p className="text-sm text-slate-400 mb-6">
          These details will appear on invoices for customers who pay by bank transfer.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Bank Name */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Bank Name
          </label>
          <input
            type="text"
            value={formData.bank_name || ''}
            onChange={(e) => onChange('bank_name', e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            placeholder="ANZ Bank"
          />
        </div>

        {/* Account Name */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Account Name
          </label>
          <input
            type="text"
            value={formData.bank_account_name || ''}
            onChange={(e) => onChange('bank_account_name', e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            placeholder="Your Company Ltd"
          />
        </div>

        {/* Account Number */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Account Number
          </label>
          <input
            type="text"
            value={formData.bank_account_number || ''}
            onChange={(e) => onChange('bank_account_number', e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            placeholder="01-0123-0123456-00"
          />
          <p className="text-xs text-slate-500 mt-1">
            NZ bank account format: XX-XXXX-XXXXXXX-XX
          </p>
        </div>
      </div>

      {/* Note */}
      <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
        <p className="text-sm text-blue-300">
          <strong>Note:</strong> Bank details are only shown on invoices. For online payments,
          customers will be directed to your configured Stripe checkout.
        </p>
      </div>
    </div>
  );
}

export default AdminSettingsPage;
